import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { getAdminUsers, type AdminUserRow } from "../../../services/adminOverviewApi";
import {
  liveCardClassName,
  liveFigureClassName,
  liveOptionTitleClassName,
  liveSmallTextClassName,
} from "../LiveStrategies/liveStrategiesUi";

const PAGE = 50;
const n = (v: number | null | undefined) => (v == null ? "–" : v.toLocaleString());
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "–");

function aiLeft(u: AdminUserRow): string {
  if (u.ai.free_prompts_left != null) return `${u.ai.free_prompts_left} prompts`;
  const total = (u.ai.plan_credits_left ?? 0) + u.ai.packs_left;
  return `${n(total)} credits${u.ai.packs_left ? ` (${n(u.ai.packs_left)} bought)` : ""}`;
}

const HEADERS = ["User", "Plan", "Joined", "Last sign-in", "TES prompts", "AI cost", "AI left", "Backtests",
  "Running", "Trades", "Packs"];

/** Every user with their plan, what they've used and what's left (newest first, searchable). */
export default function AdminUsersPanel() {
  const [search, setSearch] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [rows, setRows] = React.useState<AdminUserRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    getAdminUsers({ search: query, limit: PAGE, offset, days: 30 })
      .then((res) => {
        if (!live) return;
        setRows(res.users);
        setTotal(res.total);
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : "Couldn't load users."))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [query, offset]);

  return (
    <div className="flex flex-col gap-4" data-admin-users="">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setOffset(0);
          setQuery(search.trim());
        }}
      >
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email, name or user id" className="h-9 max-w-xs" />
        <Button type="submit" size="sm" variant="outline-brand" data-font="primary" className="shadow-none">
          <span className="normal-case tracking-normal">Search</span>
        </Button>
        <span className={liveSmallTextClassName}>
          {loading ? "Loading…" : `${n(total)} users · usage over the last 30 days`}
        </span>
      </form>
      {error ? <p className="m-0 text-sm text-red-400">{error}</p> : null}

      <div className={cn(liveCardClassName, "overflow-hidden p-0")}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {HEADERS.map((h) => (
                <TableHead key={h} className="whitespace-nowrap font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:text-white">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.clerk_user_id} data-admin-user={u.clerk_user_id}>
                <TableCell className="min-w-56">
                  <div className={liveOptionTitleClassName}>{u.email ?? u.clerk_user_id}</div>
                  <div className={liveSmallTextClassName}>{u.name ?? ""}{u.is_admin ? " · admin" : ""}</div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className={cn(liveFigureClassName, "capitalize")}>{u.plan}</div>
                  {u.subscription.cancel_at_period_end ? (
                    <div className="font-[family-name:var(--font-primary)] text-xs text-red-400">
                      Cancels {day(u.subscription.current_period_end)}
                    </div>
                  ) : u.subscription.status && u.plan === "free" ? (
                    <div className={liveSmallTextClassName}>{u.subscription.tier} {u.subscription.status}</div>
                  ) : null}
                  {Object.keys(u.addons).length ? (
                    <div className={liveSmallTextClassName}>
                      {Object.entries(u.addons).map(([k, v]) => `${k.replace("_", " ")} ×${v}`).join(", ")}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className={cn(liveFigureClassName, "whitespace-nowrap")}>{day(u.joined_at)}</TableCell>
                <TableCell className={cn(liveFigureClassName, "whitespace-nowrap")}>{day(u.last_login_at)}</TableCell>
                <TableCell className={liveFigureClassName}>{n(u.ai.turns)}</TableCell>
                <TableCell className={cn(liveFigureClassName, "whitespace-nowrap")}>${u.ai.cost_usd.toFixed(2)}</TableCell>
                <TableCell className={cn(liveFigureClassName, "whitespace-nowrap")}>{aiLeft(u)}</TableCell>
                <TableCell className={liveFigureClassName}>{n(u.backtests)}</TableCell>
                <TableCell className={liveFigureClassName}>{n(u.deployments_running)}</TableCell>
                <TableCell className={liveFigureClassName}>{n(u.trades)}</TableCell>
                <TableCell className={liveFigureClassName}>{n(u.packs_bought)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline-gray" data-font="primary" className="shadow-none"
          disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
          <span className="normal-case tracking-normal">Previous</span>
        </Button>
        <Button type="button" size="sm" variant="outline-gray" data-font="primary" className="shadow-none"
          disabled={offset + PAGE >= total || loading} onClick={() => setOffset(offset + PAGE)}>
          <span className="normal-case tracking-normal">Next</span>
        </Button>
      </div>
    </div>
  );
}
